import AddIcon from "@mui/icons-material/AddRounded";
import type { AutocompleteProps, SlotCommonProps } from "@mui/material";
import { Autocomplete, Button, Divider, List, ListItem, Typography } from "@mui/material";
import type { HTMLAttributes, Ref } from "react";
import useLazyRenderList from "../hooks/useLazyRenderList";

interface Props<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> {
  withLazyRendering?: boolean;
  noItemsText?: string;
  onCreateOption?: () => void;
}

const NEW_OPTION_BUTTON_ID = "new_option_button";

const NewOptionButton = ({ onClick }: { onClick?: () => void }) => {
  return (
    <Button sx={{ ml: 2, my: 1 }} startIcon={<AddIcon />} variant="text" id={NEW_OPTION_BUTTON_ID} onClick={onClick}>
      Create New
    </Button>
  );
};

interface ListboxProps extends HTMLAttributes<HTMLElement> {
  ref?: Ref<HTMLUListElement>;
}

const Listbox = ({ ref, ...props }: ListboxProps) => {
  const { children, ...other } = props;

  return (
    <List role="listbox" ref={ref} {...other}>
      {children}
    </List>
  );
};

const ListboxLazyRender = ({ ref, ...props }: ListboxProps) => {
  const { children, onScroll, ...other } = props;
  const { loadMore, loadedItems } = useLazyRenderList({ children });

  // MUI's Autocomplete supplies its own listbox `onScroll` (touch-scroll tracking) via props.
  // Compose it with `loadMore` so lazy loading keeps working; otherwise the spread would drop one.
  const handleScroll: React.UIEventHandler<HTMLElement> = (event) => {
    onScroll?.(event);
    loadMore();
  };

  return (
    <List role="listbox" ref={ref} {...other} onScroll={handleScroll}>
      {loadedItems}
    </List>
  );
};

const ListboxWithNewOption = ({ ref, ...props }: ListboxProps) => {
  const { children, ...other } = props;

  return (
    <>
      <List role="listbox" ref={ref} {...other}>
        {children}
      </List>
      <Divider />
      <NewOptionButton />
    </>
  );
};

const ListboxLazyRenderWithNewOption = ({ ref, ...props }: ListboxProps) => {
  const { children, onScroll, ...other } = props;
  const { loadMore, loadedItems } = useLazyRenderList({ children });

  // See ListboxLazyRender: compose MUI's listbox onScroll with loadMore.
  const handleScroll: React.UIEventHandler<HTMLElement> = (event) => {
    onScroll?.(event);
    loadMore();
  };

  return (
    <>
      <List role="listbox" ref={ref} {...other} onScroll={handleScroll}>
        {loadedItems}
      </List>
      <Divider />
      <NewOptionButton />
    </>
  );
};

const AutocompleteCreatable = <
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  withLazyRendering,
  noItemsText,
  onCreateOption,
  ...props
}: Props<Value, Multiple, DisableClearable, FreeSolo>) => {
  const listboxComponent =
    onCreateOption !== undefined
      ? withLazyRendering
        ? ListboxLazyRenderWithNewOption
        : ListboxWithNewOption
      : withLazyRendering
        ? ListboxLazyRender
        : Listbox;

  return (
    <Autocomplete
      {...props}
      slotProps={{
        ...props.slotProps,
        listbox: { component: listboxComponent },
        paper: {
          ...props.slotProps?.paper,
          sx: { ...(props.slotProps?.paper as SlotCommonProps)?.sx, ".MuiAutocomplete-noOptions": { p: 0 } },
        },
      }}
      onClose={(event, reason) => {
        if ("relatedTarget" in event && (event.relatedTarget as HTMLElement)?.id === NEW_OPTION_BUTTON_ID) {
          onCreateOption?.();
        } else {
          props.onClose?.(event, reason);
        }
      }}
      noOptionsText={
        <>
          <ListItem sx={{ py: 2 }}>
            <Typography>{noItemsText || "No items"}</Typography>
          </ListItem>
          {onCreateOption !== undefined && (
            <>
              <Divider />
              <NewOptionButton onClick={onCreateOption} />
            </>
          )}
        </>
      }
    />
  );
};

export default AutocompleteCreatable;
